import { get } from "helpers/api_helper"
import {
  NumberToWordsConverter,
  customFormatDate,
  teethsTable,
} from "helpers/basic_helper"
import moment from "moment"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { companyName } from "helpers/company_name"

const loadingStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
}

const styleCan = {
  color: "red",
  position: "absolute",
  top: "50%",
  fontSize: "92px",
  left: "calc(100% - 62%)",
  transform: "rotate(322deg)",
  opacity: 0.1,
}

const InvoicePdfview = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)

  const [printData, setPrintData] = useState(null)
  const [orders, setOrders] = useState(null)
  const [invoiceType, setInvoiceType] = useState("TAX INVOICE")
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        setInvoiceType(
          `${queryParams.get("type")
            ? `${queryParams.get("type").toUpperCase()} `
            : "TAX"
          } INVOICE`
        )
        const res = await get(
          `/api/invoice/invoicePrintData/${queryParams.get("type") ? 1 : 2
          }/${queryParams.get("invoice_number")}`
        )

        setPrintData(res?.response)

        const ordersList = res?.response?.orders || []
        const grouped = ordersList.reduce((acc, order) => {
          const doc = (order.doctor || "").trim()
          const key = doc.replaceAll(" ", "_")
          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(order)
          return acc
        }, {})

        setOrders(grouped)
      } catch (err) {
        console.error("Failed to load invoice print data:", err)
      }
    }

    fetchInvoiceData()
  }, [])

  const printPage = () => {
    const printableElement = document.getElementById("printablediv")
    if (!printableElement) return

    let iframe = document.getElementById("clean-print-frame")
    if (iframe) iframe.remove()

    iframe = document.createElement("iframe")
    iframe.id = "clean-print-frame"
    iframe.style.position = "fixed"
    iframe.style.right = "0"
    iframe.style.bottom = "0"
    iframe.style.width = "0"
    iframe.style.height = "0"
    iframe.style.border = "0"
    document.body.appendChild(iframe)

    const doc = iframe.contentWindow.document
    doc.open()
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${invoiceType} - ${queryParams.get("invoice_number") || ""}</title>
          <style>
            @page {
              size: A4;
              margin: 6mm;
            }
            * {
              box-sizing: border-box;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
            }
            body {
              margin: 0;
              padding: 0;
              font-size: 11px;
              color: #000;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-invoice-container {
              width: 100%;
              margin: 0 auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            .table-bordered th, .table-bordered td {
              border: 1px solid #00000045;
              padding: 3px 5px;
            }
            .row {
              display: flex;
              flex-wrap: wrap;
            }
            .col-sm-6 { width: 50%; }
            .col-sm-7 { width: 58.33%; }
            .col-sm-5 { width: 41.67%; }
            .text10 { font-size: 10px; }
            .p-0 { padding: 0 !important; }
            .ps-1 { padding-left: 4px !important; }
            img { max-width: 100%; height: auto; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printableElement.innerHTML}
        </body>
      </html>
    `)
    doc.close()

    iframe.contentWindow.focus()
    setTimeout(() => {
      iframe.contentWindow.print()
      setTimeout(() => {
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe)
        }
      }, 1000)
    }, 150)
  }

  const downloadPdf = async () => {
    const container =
      document.querySelector(".print-invoice-container") ||
      document.getElementById("printablediv")
    if (!container || isDownloading) return

    try {
      setIsDownloading(true)
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ])

      // Scroll to top so html2canvas captures from top-left (0,0)
      window.scrollTo(0, 0)

      // Ensure all images in the container have completed loading
      const images = Array.from(container.querySelectorAll("img"))
      await Promise.all(
        images.map(
          img =>
            new Promise(resolve => {
              if (img.complete) resolve()
              else {
                img.onload = () => resolve()
                img.onerror = () => resolve()
              }
            })
        )
      )

      // Temporarily normalize container styles for clean A4 snapshot
      const origMinHeight = container.style.minHeight
      const origBoxShadow = container.style.boxShadow

      container.style.minHeight = "auto"
      container.style.boxShadow = "none"

      await new Promise(r => setTimeout(r, 60))

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
      })

      // Restore original container styles
      container.style.minHeight = origMinHeight
      container.style.boxShadow = origBoxShadow

      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth() // 210 mm
      const pdfHeight = pdf.internal.pageSize.getHeight() // 297 mm

      // Use clean 6mm margins (same as print @page margin: 6mm)
      const marginX = 6
      const marginY = 6
      const printableWidth = pdfWidth - marginX * 2 // 198 mm
      const printableHeight = pdfHeight - marginY * 2 // 285 mm

      const imgWidth = printableWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      // If invoice fits on 1 page (or is within 15% overflow), scale gently to fit 1 page
      if (imgHeight <= printableHeight * 1.15) {
        let finalWidth = imgWidth
        let finalHeight = imgHeight

        if (finalHeight > printableHeight) {
          const fitScale = printableHeight / finalHeight
          finalWidth = finalWidth * fitScale
          finalHeight = printableHeight
        }

        const xPos = marginX + (printableWidth - finalWidth) / 2
        const imgData = canvas.toDataURL("image/png")
        pdf.addImage(imgData, "PNG", xPos, marginY, finalWidth, finalHeight)
      } else {
        // Multi-page invoice: slice canvas into clean page chunks
        const pageCanvasHeight = (printableHeight * canvas.width) / printableWidth
        let yOffset = 0
        let pageNum = 0

        while (yOffset < canvas.height) {
          const sliceHeight = Math.min(pageCanvasHeight, canvas.height - yOffset)

          const pageCanvas = document.createElement("canvas")
          pageCanvas.width = canvas.width
          pageCanvas.height = sliceHeight
          const ctx = pageCanvas.getContext("2d")
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)

          ctx.drawImage(
            canvas,
            0,
            yOffset,
            canvas.width,
            sliceHeight,
            0,
            0,
            pageCanvas.width,
            sliceHeight
          )

          const pageImgData = pageCanvas.toDataURL("image/png")
          const pageSliceHeightMm = (sliceHeight * printableWidth) / canvas.width

          if (pageNum > 0) {
            pdf.addPage()
          }
          pdf.addImage(pageImgData, "PNG", marginX, marginY, printableWidth, pageSliceHeightMm)

          yOffset += sliceHeight
          pageNum++
        }
      }

      const invoiceNum = queryParams.get("invoice_number") || "invoice"
      const cleanType = (invoiceType || "invoice").toLowerCase().replace(/\s+/g, "_")
      pdf.save(`${cleanType}_${invoiceNum}.pdf`)
    } catch (error) {
      console.error("Error generating PDF:", error)
      alert("Failed to download PDF. Please use the Print button to Save as PDF.")
    } finally {
      setIsDownloading(false)
    }
  }

  let total_units = 0
  let total_amount = 0
  let products = []

  if (orders) {
    Object.keys(orders).forEach((v, f) => {
      // Create header row for each doctor
      products.push(
        <tr key={`op_header_${f}`}>
          <td colSpan={10} style={{ fontSize: "10px", fontWeight: "bold" }}>
            Requested By {v.replaceAll("_", " ")}
          </td>
        </tr>
      )

      orders[v]?.forEach((pv, i) => {
        const total = parseFloat(pv.product_unit) * parseFloat(pv.product_price)
        total_units += parseFloat(pv.product_unit)
        total_amount += total

        products.push(
          <tr key={`op_${v}_${i}`}>
            <td className="text10 p-0 ps-1">{i + 1}</td>
            <td className="text10 p-0 ps-1">{pv.order_number}</td>
            <td className="text10 p-0 ps-1">{pv.orderform_number}</td>
            <td className="text10 p-0 ps-1">
              {customFormatDate(pv.orderform_date, "DD/MM/YYYY")}
            </td>
            <td className="text10 p-0 ps-1">{pv.patient_name}</td>
            <td className="text10 p-0 ps-1">{pv.product}</td>
            <td
              className="text10 p-0"
              style={{ padding: "0px", width: "auto", verticalAlign: "middle" }}
            >
              {teethsTable(JSON.parse(pv.teeth))}
            </td>
            <td className="text10 p-0 ps-1">{pv.product_unit}</td>
            <td className="text10 p-0 ps-1">
              {parseFloat(pv.product_price).toFixed(2)}
            </td>
            <td className="text10 p-0 ps-1" style={{ textAlign: "right" }}>
              {total.toFixed(2)}
            </td>
          </tr>
        )
      })
    })
  }

  const getGstValue = () => {
    const { invoice } = printData
    const gstValue = parseFloat(invoice.gst)
    const isPanIndia = printData.invoice.is_panindia > 0

    if (isPanIndia) {
      return (
        <div
          className="row"
          style={{ borderBottom: "1px solid #000", margin: "1px -5px" }}
        >
          <div className="col-sm-7" style={{ textAlign: "right" }}>
            IGST @ {gstValue.toFixed(2)} %
          </div>
          <div className="col-sm-5" style={{ textAlign: "right" }}>
            {((total_amount * gstValue) / 100).toFixed(2)}
          </div>
        </div>
      )
    } else {
      const cgstValue = (gstValue / 2).toFixed(2)
      return printData?.org?.state_code != printData?.client?.state_code ? (
        <>
          <div
            className="row"
            style={{ borderBottom: "1px solid #000", margin: "2px -5px" }}
          >
            <div className="col-sm-7" style={{ textAlign: "right" }}>
              IGST @ 5%
            </div>
            <div className="col-sm-5" style={{ textAlign: "right" }}>
              {((total_amount * gstValue) / 100).toFixed(2)}
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="row"
            style={{ borderBottom: "1px solid #000", margin: "2px -5px" }}
          >
            <div className="col-sm-7" style={{ textAlign: "right" }}>
              CGST @ {cgstValue} %
            </div>
            <div className="col-sm-5" style={{ textAlign: "right" }}>
              {((total_amount * (gstValue / 2)) / 100).toFixed(2)}
            </div>
          </div>
          <div
            className="row"
            style={{ borderBottom: "1px solid #000", margin: "4px -5px" }}
          >
            <div className="col-sm-7" style={{ textAlign: "right" }}>
              SGST @ {cgstValue} %
            </div>
            <div className="col-sm-5" style={{ textAlign: "right" }}>
              {((total_amount * (gstValue / 2)) / 100).toFixed(2)}
            </div>
          </div>
        </>
      )
    }
  }

  const getGrandTotal = () => {
    const { invoice } = printData
    const gstAmount = (parseFloat(total_amount) * parseFloat(invoice.gst)) / 100
    const total = parseFloat(total_amount) + gstAmount
    const decimal = total - Math.floor(total)

    if (decimal < 0.5) {
      return Math.floor(total)
    } else {
      return Math.ceil(total)
    }
  }

  const getINVFOR = () => {
    const num =
      printData?.[`${queryParams.get("type") ? "" : "tax_"}invoice`]
        ?.invoice_number

    if (queryParams.get("type")) {
      return num
    } else {
      return `${num.slice(0, 4)} - ${num.slice(4, 6)} / ${num.slice(6)}`
    }
  }


  return printData !== null ? (
    <>
      <div className="d-print-none" style={{ width: "100%", textAlign: "center", margin: "22px auto" }}>
        <button
          onClick={() => printPage()}
          className="btn btn-primary"
          type="button"
          style={{ width: "130px", height: "46px", marginRight: "12px" }}
        >
          <i className="fas fa-print me-2"></i>Print
        </button>
        {/* <button
          onClick={() => downloadPdf()}
          disabled={isDownloading}
          className="btn btn-success"
          type="button"
          style={{ width: "160px", height: "46px" }}
        >
          <i className={`fas ${isDownloading ? "fa-spinner fa-spin" : "fa-file-download"} me-2`}></i>
          {isDownloading ? "Saving..." : "Download PDF"}
        </button> */}
      </div>
      <div id="printablediv">
        <div
          className="print-invoice-container print-container"
          style={{ margin: " 0 auto", position: "relative" }}
        >
          {printData?.invoice?.status == 1 && (
            <h1 style={styleCan}>Cancelled</h1>
          )}
          <div>
            <div className="row">
              <div style={{ width: "20%", textAlign: "center" }}>
                <img src={printData?.org?.logo} style={{ width: "148px" }} />{" "}
                <br />
                <br />
                {/* <h2 style={{paddingTop: '10px'}}>{queryParams?.get('type') ? 'PROFORMA ': 'TAX '} INVOICE</h2>
                                <div className='position-absolute' style={{right: '10px', top: '10px', padding: '6px', fontWeight: 'bold'}}>Pirntdate# 26/12/2022</div> */}
              </div>
              <div style={{ width: "40%", textAlign: "left" }}>
                <span style={{ fontSize: "22px", fontWeight: "600" }}>
                  {companyName(
                    printData?.org?.title,
                    printData?.[
                      `${queryParams.get("type") ? "" : "tax_"}invoice`
                    ]?.invoice_date
                  )?.toUpperCase()}
                </span>
                <br />
                <address style={{ width: "300px", marginBottom: "0" }}>
                  {printData?.org?.address}
                </address>
                <span>
                  GSTIN:{" "}
                  {companyName(
                    printData?.org?.gst,
                    printData?.[
                      `${queryParams.get("type") ? "" : "tax_"}invoice`
                    ]?.invoice_date,
                    "gst"
                  )?.toUpperCase()}
                </span>
                <br />
                <span>HSN Number: {printData?.org?.hsn}</span>
                <br />
              </div>
              <div
                style={{ width: "40%", textAlign: "right", marginTop: "30px" }}
              >
                <span>Mobile: {printData?.org?.mobile} (ONLY BILLING) </span>
                <br />
                <span>E-Mail: {printData?.org?.ac_email}</span>
                <br />
              </div>
            </div>

            <div className="row">
              <div
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginTop: "6px",
                }}
              >
                <h4>{invoiceType}</h4>
              </div>
            </div>

            <div style={{ width: "100%", display: "flex" }}>
              <div style={{ width: "60%" }}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: printData?.client?.clientinfo,
                  }}
                />
              </div>

              <div style={{ width: "40%", textAlign: "right" }}>
                {queryParams.get("type")
                  ? ""
                  : (() => {
                    const list = (printData?.pil || "")
                      .split(",")
                      .map(x => x.trim())
                      .filter(Boolean)

                    // First line: first two values (if available)
                    let first = ""
                    if (list.length === 1) {
                      first = list[0]
                    } else if (list.length >= 2) {
                      first = `${list[0]},${list[1]}`
                    }

                    // Remaining list after first two
                    const rest = list.slice(2)

                    // Chunk into pairs
                    const chunks = []
                    for (let i = 0; i < rest.length; i += 2) {
                      chunks.push(rest.slice(i, i + 2))
                    }

                    return (
                      <>
                        <strong>Proforma Invoice #.</strong>{" "}
                        <span>{first}</span>
                        <br />
                        {chunks.map((pair, idx) => (
                          <div key={idx}>{pair.join(" ")}</div>
                        ))}
                      </>
                    )
                  })()}
                <strong>Invoice #.</strong> <span>{getINVFOR()}</span>
                <br />
                <strong>Date</strong>{" "}
                <span>
                  {moment(
                    printData?.[
                      `${queryParams.get("type") ? "" : "tax_"}invoice`
                    ]?.invoice_date
                  ).format("DD-MM-YYYY")}
                </span>
              </div>
            </div>

            <table className="table table-bordered">
              <thead>
                <tr>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    #
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Order #
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Challan #
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Challan Date
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Patient
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Product
                  </th>
                  <th style={{ fontSize: "12px", width: "90px" }} className="p-0 ps-1">
                    Teeth
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Units
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Rate/Unit{" "}
                  </th>
                  <th style={{ fontSize: "12px" }} className="p-0 ps-1">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {products}
                <tr style={{ fontWeight: "700" }}>
                  <td colSpan={7} style={{ textAlign: "right" }}>
                    Sub Total:{" "}
                  </td>
                  <td>{total_units}</td>
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    {total_amount.toFixed(2)}
                  </td>
                </tr>
                <tr rowSpan={2}>
                  <td colSpan={5}>
                    <b>
                      GSTIN :{" "}
                      {companyName(
                        printData?.org?.gst,
                        printData?.[
                          `${queryParams.get("type") ? "" : "tax_"}invoice`
                        ]?.invoice_date,
                        "gst"
                      )?.toUpperCase()}
                    </b>
                    <br />
                    <b>HSN Number: {printData?.org?.hsn}</b>
                    <br />
                    <b>
                      State : {printData?.org?.state} State Code:{" "}
                      {printData?.org?.state_code}
                    </b>
                  </td>
                  <td colSpan={5}>
                    {getGstValue()}
                    <div
                      className="row"
                      style={{ marginTop: "6px", marginBottom: "0px" }}
                    >
                      <div
                        className="col-sm-7"
                        style={{ textAlign: "right", fontWeight: "700" }}
                      >
                        Grand Total
                      </div>
                      <div
                        className="col-sm-5"
                        style={{ textAlign: "right", fontWeight: "700" }}
                      >
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "INR",
                        }).format(getGrandTotal())}
                      </div>
                    </div>
                  </td>
                </tr>
                {/* <th className='p-1'>{printData?.invoice.igst > 0 ? parseFloat(printData?.invoice.igst) : (parseFloat(printData?.invoice?.cgst) + parseFloat(printData?.invoice?.sgst)).toFixed(2) }</th> */}
                <tr>
                  <td colSpan={12}>
                    <strong>
                      Grand Total (₹):{" "}
                      <NumberToWordsConverter number={getGrandTotal()} />.
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="row">
              <div className="col-sm-6">
                <span
                  dangerouslySetInnerHTML={{ __html: printData?.org?.bank }}
                />
                {/* <span>BANKER NAME:</span><br />
                                <span>{printData?.org?.bank?.bank}{' '}{printData?.org?.bank?.branch}</span><br />
                                <span>RTGS/NEFT IFS Code: {printData?.org?.bank?.ifscode}</span><br />
                                <span>C/A no.{printData?.org?.bank?.account} </span> */}
              </div>
              <div className="col-sm-6" style={{ textAlign: "right" }}>
                <span style={{ bottom: "16px" }}>Authorized</span>
                <br />
                <img width="120" src={printData?.org?.qrlink} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <div style={loadingStyle}>
      <img
        src="https://apiv2.dgddl.in/uploads/241002/241002_print.png"
        style={{ width: "92px", marginRight: "16px", float: "left" }}
      />
      <br />
      <br />
      <br />
      <br />
      <span>Loading Data...</span>
    </div>
  )
}

export default InvoicePdfview
